import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'
import { ErrorState, Spinner } from './states'

// Plays a visit's session recording from Rybbit (rrweb events, via
// /api/admin/analytics/sessions/:id/replay). rrweb-player and its stylesheet
// are loaded on first use: most dashboard visits never open a replay, and the
// player is the heaviest thing on the page.

interface ReplayResponse {
	events: { type: number; timestamp: number; data: unknown }[]
}

const CONTROLS_HEIGHT = 80
const MIN_HEIGHT = 320

interface PlayerInstance {
	$destroy?: () => void
}

export function ReplayPlayer({ sessionId }: { sessionId: string }) {
	const { data, isPending, error, refetch } = useQuery({
		queryKey: ['journey-replay', sessionId],
		queryFn: () => apiFetch<ReplayResponse>(`/api/admin/analytics/sessions/${encodeURIComponent(sessionId)}/replay`),
		// Recordings don't change once the visit is over, and run to several MB.
		staleTime: Number.POSITIVE_INFINITY,
	})
	const mount = useRef<HTMLDivElement>(null)
	const [playerError, setPlayerError] = useState<string | null>(null)

	useEffect(() => {
		const el = mount.current
		if (!data || !el) return
		// rrweb needs a full page snapshot (type 2) to draw anything.
		if (data.events.length < 2 || !data.events.some((e) => e.type === 2)) {
			setPlayerError('This recording is too short to play.')
			return
		}

		let player: PlayerInstance | null = null
		let cancelled = false
		;(async () => {
			try {
				const [{ default: RrwebPlayer }] = await Promise.all([import('rrweb-player'), import('rrweb-player/dist/style.css')])
				if (cancelled) return
				el.replaceChildren()
				const width = el.clientWidth
				// Fit the video AND the controls below it (~80px) into what's left
				// of the viewport, so play/seek never sit off-screen.
				const room = window.innerHeight - el.getBoundingClientRect().top - CONTROLS_HEIGHT - 24
				const height = Math.max(MIN_HEIGHT, Math.min(Math.round(width * 0.6), room))
				player = new RrwebPlayer({
					target: el,
					props: {
						// biome-ignore lint/suspicious/noExplicitAny: rrweb's eventWithTime union; the data is Rybbit's rrweb recording as-is
						events: data.events as any,
						width,
						height,
						autoPlay: false,
						skipInactive: true,
						showController: true,
					},
				}) as PlayerInstance
			} catch (err) {
				if (!cancelled) setPlayerError(err instanceof Error ? err.message : 'The player could not start.')
			}
		})()

		return () => {
			cancelled = true
			player?.$destroy?.()
			el.replaceChildren()
		}
	}, [data])

	if (isPending) return <Spinner label="Loading the recording from Rybbit…" />
	if (error) return <ErrorState error={error} onRetry={refetch} />
	if (playerError) return <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{playerError}</p>
	// shrink-0: inside the panel's flex column an overflow-hidden child would
	// otherwise be squeezed to the remaining height, cutting off the controls
	// instead of letting the panel scroll.
	return <div ref={mount} className="w-full shrink-0 overflow-hidden rounded-lg border border-line bg-canvas" />
}
